import { defineComponent as _defineComponent } from 'vue'
import { isEmpty } from 'lodash'
import moment from 'moment'
import length from '@turf/length'
import { computed, ref, toRef, watchEffect } from 'vue'
import { useRoute } from 'vue-router/composables'
import {
  createNamespacedHelpers,
  useGetters,
  useStore,
} from 'vuex-composition-helpers'

import { useGetJobGeoJson } from '@/api/field-view'
import { useCurrentOrg } from '@/common/useCurrentOrg'
import Job, { APPROVED, NO_APPROVAL, PENDING_APPROVAL } from '@/models/job'
import JobAssignmentFormRequirement from '@/models/job-assignment-form-requirement'
import JobHistory from '@/models/job-history'
import JobComment from '@/models/job-comment'
import { CustomFieldsSchema } from '@/models/types/custom-fields-schema'
import { TripMapDistanceUnits } from '@/store/modules/organization-config'
import { kmToMilesConversion } from '@/utils/constants'
import {
  formatDateTime,
  formatDurationAsHours,
  formatTimeRange,
} from '@/utils/date-formatting'
import { extractFieldsFromSchema } from '@/utils/field-schema-helper'
import { mergeJsonSchemas } from '@/utils/json-schema-merger.js'

import BatchSelectionPage from '@/components/batches/BatchSelectionPage.vue'
import FormLayout from '@/components/common/FormLayout.vue'
import FormSubmission from '@/components/forms/FormSubmission.vue'
import NewFormSelectionBottomSheet from '@/components/forms/NewFormSelectionBottomSheet.vue'
import SchemaBasedForm from '@/components/forms/SchemaBasedForm.vue'
import ApprovalRejectionForm from '@/components/jobs/ApprovalRejectionForm.vue'
import JobCardLocationsDetail from '@/components/jobs/JobCardLocationsDetail.vue'
import JobSignature from '@/components/jobs/sections/JobSignature.vue'
import JobSignatureForm from '@/components/jobs/JobSignatureForm.vue'
import LineItemForm from '@/components/jobs/line-items/LineItemForm.vue'
import LineItemsCard from '@/components/jobs/line-items/LineItemsCard.vue'
import ProjectBreadcrumbs from '@/components/project/ProjectBreadcrumbs.vue'
import VsSnackbar from '@/components/vision/VsSnackbar.vue'
import { SnackType } from '@/components/vision/SnackBarType'
import VsText from '@/components/vision/VsText.vue'
import JobActivityLog from '@/components/tickets/history/JobActivityLog.vue'

import AssignmentFormRequirements from './AssignmentFormRequirements.vue'
import AttachmentCard from './AttachmentCard.vue'
import AttachmentPicker from './AttachmentPicker.vue'
import Attachments from './Attachments.vue'
import JobDetailsEdit from './JobDetailsEdit.vue'
import JobInlineForm from './JobInlineForm.vue'
import JobLocationsAndInventoryEditSaveWrapper from './JobLocationsAndInventoryEditSaveWrapper.vue'
import JobPlannedActualTimesEdit from './JobPlannedActualTimesEdit.vue'
import JobStatusChip from './JobStatusChip.vue'
import JobTagsEdit from './JobTagsEdit.vue'
import JobTripMap from './JobTripMap.vue'
import ReadonlyJobFormActionBar from './ReadonlyJobFormActionBar.vue'
import SendEmailDialog from './SendEmailDialog.vue'
import SendForApprovalBottomSheet from './SendForApprovalBottomSheet.vue'
import VsBottomSheet from '@/components/vision/VsBottomSheet.vue'
import JobReferencesCard from '@/components/jobs/references/JobReferencesCard.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'ReadonlyJobForm',
  props: {
    id: null,
    parentRouteName: null,
    jobUuid: { default: undefined },
    isFetching: { type: Boolean, default: true },
    showTripMap: { type: Boolean, default: false },
    mapLink: { default: undefined },
    loadHistoryError: { type: Boolean, default: false },
    history: null,
    comments: null,
    extensionSchemaId: { default: undefined },
    extensionSchema: { default: undefined },
    hasNewerCustomFieldsSchema: { type: Boolean, default: false }
  },
  emits: ["dismiss", "refresh-job-history", "create-comment", "update-readonly-job", "update-schema-button-clicked", "reset-custom-fields-schema-id"],
  setup(__props: any, { expose, emit }: { emit: ({
  (e: 'dismiss')
  (e: 'refresh-job-history')
  (e: 'create-comment', comment)
  (e: 'update-readonly-job')
  (e: 'update-schema-button-clicked')
  (e: 'reset-custom-fields-schema-id')
}), expose: any, slots: any, attrs: any }) {

const props = __props as { id: number, parentRouteName: string, jobUuid: string | undefined, isFetching: boolean, showTripMap: boolean, mapLink: string | null, loadHistoryError: boolean, history: JobHistory[] | null, comments: JobComment[], extensionSchemaId: number | null, extensionSchema: CustomFieldsSchema | null, hasNewerCustomFieldsSchema: boolean }



const isEditingPlannedActualTimes = ref(false)
const isEmailDialogVisible = ref(false)
const isSendingEmailForApproval = ref(false)
const isEditingDetails = ref(false)
const isEditingLocationsAndInventory = ref(false)
const isEditingJobFormSubmission = ref(false)
const isFillingFormRequirementSubmission = ref(false)
const isEditingTags = ref(false)
const selectedFormSubmissionId = ref<string | null>(null)
const isSelectingForm = ref(false)
const selectedFormId = ref<string | null>(null)
const selectedFormRequirement = ref<JobAssignmentFormRequirement | null>(null)
const isSnackbarVisible = ref(false)
const customSnackbarMessage = ref()
const tripDistance = ref<number | undefined>(undefined)
const snackbarMessageKey = ref<string | null>(null)
const snackbarType = ref<SnackType>(SnackType.INFO)
const isEditingLineItems = ref(false)
const isAddingToBatch = ref(false)
const isRejectingApproval = ref(false)
const isAddingSignature = ref(false)
const snackbarMessages = {
  updatedAttachedFormSubmission: 'Form submission has been updated',
  unsupportedAttachmentType: 'This file type is not supported',
  unsupportedAttachmentSize: 'File size must be under 25 MB',
  errorUploadingAttachment: 'Failed to upload attachment',
  submittedForApproval: 'Job submitted for approval',
  submitForApprovalFailed: 'Failed to submit job for approval',
  cancelApprovalRequest: 'Successfully cancel approval request',
  cancelApprovalRequestFailed: 'Failed to cancel approval request',
  unCompleteJobRequest: 'Job reopened',
  unCompleteJobRequestFailed: 'Failed to reopen job',
  approved: 'Job approved',
  unapproved: 'Job unapproved',
  rejected: 'Job approval rejected',
  approvalFailed: 'Failed to approve the job',
  unapprovalFailed: 'Failed to unapprove the job',
  archived: 'Job archived',
  archiveFailed: 'Failed to archive the job',
  unarchived: 'Job unarchived',
  unarchiveFailed: 'Failed to unarchive the job',
  notificationSent: 'Notifications sent',
  errorFetchingJobHistory: 'Failed to fetch job history',
  detailsUpdated: 'Job details has been updated',
  plannedOrActualTimesUpdated: 'Job planned/actual times have been updated',
  signatureAdded: 'Signature added',
  signatureCleared: 'Signature cleared',
  locationsInventoryUpdated: 'Job locations/inventory has been updated',
  tagsUpdated: 'Tags have been updated',
  pdfDownloadError: 'Failed to download PDF',
  attachedForm: 'Form attached to the job',
  attachFormError: 'Failed to attach form to the job',
  attachedFormRequirement: 'Form attached to the job',
  attachFormRequirementError: 'Failed to attach form to the job',
  updatedFormRequirementSubmission: 'Form submission has been updated',
  jobAddedToBatch: 'Job added to the selected batch',
  jobAddedToBatchFailure: 'Cannot add job to the selected batch',
  removedJobFromBatch: 'Job removed from batch',
  removedJobFromBatchFailure: 'Fail to remove job from batch',
}

const store = useStore()
const route = useRoute()

const jobNumber = toRef(props, 'id')
const showTripMapRef = toRef(props, 'showTripMap')
const shouldFetchGeoJsonManually = computed(() => !showTripMapRef.value)
const { data: jobGeoJson } = useGetJobGeoJson(jobNumber, {
  enabled: shouldFetchGeoJsonManually,
})

watchEffect(() => {
  if (jobGeoJson.value && jobGeoJson.value.features.length > 0) {
    const lineStrings = jobGeoJson.value.features.filter(
      (f) => f.properties?.Type === 'Route'
    )

    const totalDistance = lineStrings.reduce((a, b) => a + length(b), 0)

    setTripDistance(totalDistance)
  }
})

const { lineItems: lineItemsFeatureFlag } = useGetters('featureFlags', [
  'lineItems',
])

const getUserBySid = computed(() => store.getters['users/getBySid'])
const canModifyCompletedJobsPendingApproval = computed<
  (hubId: string) => boolean
>(() => store.getters['permissions/canModifyCompletedJobsPendingApproval'])
const canModifyCompletedJobsWithNoApproval = computed<
  (hubId: string) => boolean
>(() => store.getters['permissions/canModifyCompletedJobsWithNoApproval'])
const getBusinessUnitById = computed(
  () => store.getters['businessUnits/getBusinessUnitById']
)
const customFieldsSchema = computed(
  () => store.getters['schemas/getJobCustomFieldsSchema']
)
const getTaskById = computed(() => store.getters['tasks/getById'])
const getCostCentersForHub = computed(
  () => store.getters['costCenters/getCostCentersForHub']
)
const getForms = computed(() => store.getters['forms/getAllVisible'])
const getFormDefinitionById = computed(() => store.getters['forms/getById'])
const getRolesForBusinessUnit = computed(
  () => store.getters['permissions/getRolesForBusinessUnit']
)
const canModifyCompletedJobActualTimes = computed(
  () => store.getters['permissions/canModifyCompletedJobActualTimes']
)
const getByChildHubId = computed(
  () => store.getters['companies/getByChildHubId']
)
const jobPlannedActualTimesEditingEnabled = computed(
  () => store.state.featureFlags.all.jobPlannedActualTimesEditing
)
const tripMapDistanceUnit = computed<TripMapDistanceUnits>(
  () => store.state.organizationConfig.tripMapDistanceUnit
)

const job = computed<Job>(() => store.getters['jobs/getById'](props.id))

const isBottomSheetVisible = computed<boolean>(
  () =>
    !!selectedFormSubmissionId.value ||
    isEditingDetails.value ||
    isEditingLocationsAndInventory.value ||
    isEditingTags.value ||
    isSelectingForm.value ||
    !!selectedFormId.value ||
    isEditingPlannedActualTimes.value ||
    !!selectedFormRequirement.value ||
    isEditingLineItems.value ||
    isAddingToBatch.value ||
    isRejectingApproval.value ||
    isSendingEmailForApproval.value ||
    isAddingSignature.value
)

const displayedJobId = computed(
  () => `Job #${route.params.id} · ${company.value?.shortCode}`
)

const finalStatus = computed(() =>
  job.value.approvalStatus !== NO_APPROVAL
    ? job.value.approvalStatus
    : job.value.status.trim()
)

const projectSideSheetRoute = computed(() => {
  const routePrefix = route.name?.split('-')[0]
  return `${routePrefix}-project`
})

const title = computed<string | undefined>(() => job.value.activity?.taskName)

const subtitle = computed(() => {
  if (!job.value.resourceType || !job.value.serviceProvider) return ''

  // Requesters can't see the assigned resources
  if (!job.value.unit)
    return `Assigned to ${job.value.resourceType.name} · ${job.value.serviceProvider.shortCode}`
  return `Assigned to ${job.value.resourceType.name} · ${job.value.unit.name} · ${job.value.serviceProvider.shortCode}`
})

const jobCrewNames = computed(() => {
  const crewNames = job.value.crew.map((u) => getUserBySid.value(u)?.name)

  if (crewNames.length === 1) return crewNames[0]
  else
    return `${crewNames.slice(0, -1).join(',')} and ${
      crewNames[crewNames.length - 1]
    }`
})

const formattedPlannedTime = computed(() =>
  formatTimeRange(job.value.startTime, job.value.endTime, 'to')
)
const formattedActualTime = computed(() => {
  if (
    !job.value.activeTime ||
    moment(job.value.activeTime).isSame(
      moment(job.value.completedTime),
      'minute'
    )
  ) {
    return 'Completed ' + formatDateTime(job.value.completedTime)
  }
  return (
    'Executed ' +
    formatTimeRange(job.value.activeTime, job.value.completedTime, 'to')
  )
})

const formattedPlannedDuration = computed(() =>
  formatDurationAsHours(job.value.estimatedDuration, true)
)
const formattedActualDuration = computed(() =>
  formatDurationAsHours(moment.duration(job.value.activeDuration), true)
)
const showEditButton = computed(() => {
  return (
    !jobIsApproved.value &&
    ((job.value.isPendingApproval &&
      canModifyCompletedJobsPendingApproval.value(job.value.hubId)) ||
      canModifyCompletedJobsWithNoApproval.value(job.value.hubId))
  )
})

const costCenterDescription = computed(() => {
  return (
    getCostCentersForHub
      .value(job.value.hubId)
      .find((cc) => cc.label === job.value.costCenter)?.description ?? ''
  )
})

const jobIsApproved = computed(() => job.value.approvalStatus === APPROVED)
const jobRequesterName = computed(
  () => getUserBySid.value(job.value.customerSid)?.name ?? 'Unknown User'
)
const hub = computed(() => getBusinessUnitById.value(job.value.hubId))
const company = computed(() => getByChildHubId.value(job.value.hubId))
const jobDivisionPath = computed(() => {
  const hubString = hub.value ? `${hub.value.name} > ` : ''

  const orgString = company.value ? `${company.value.name} > ` : ''

  return `${orgString}${hubString}${job.value.division?.name}`
})

const followersLabel = computed(() =>
  job.value.followers
    .map((f) => getUserBySid.value(f)?.name ?? 'Unknown User')
    .join(', ')
)

const rootSchema = computed<CustomFieldsSchema>(() => {
  const { division, activity } = job.value

  if (isEmpty(customFieldsSchema.value) || !division || !activity) {
    return {
      schema: {},
      uiSchema: {},
    }
  }

  const task = getTaskById.value(activity.taskId)
  const hasCustomFields =
    props.extensionSchemaId && !isEmpty(props.extensionSchema)
  // Deliberate fallback to activity's latest schema, just in case we are
  // viewing a Job record created before 2024 which lack an extensionSchemaId.
  const activitySchema = hasCustomFields
    ? {
        id: props.extensionSchemaId,
        ...props.extensionSchema,
      }
    : {
        id: task?.version,
        ...task?.jobCustomFieldsSchema,
      }
  const divisionSchema = division.jobCustomFieldsSchema ?? {}

  return mergeJsonSchemas(
    activitySchema,
    divisionSchema,
    customFieldsSchema.value
  )
})

const schema = computed(() => rootSchema.value.schema)
const uiSchema = computed(() => rootSchema.value.uiSchema)

const flattenedFormFields = computed(() => {
  const formFields = {}
  extractFieldsFromSchema(schema.value, uiSchema.value, formFields)
  return formFields
})

const canDeleteFormSubmissions = computed(() => {
  if (job.value.approvalStatus === PENDING_APPROVAL) {
    return canModifyCompletedJobsPendingApproval.value(job.value.hubId)
  }
  return false
})

const fillableForms = computed(() => {
  return getForms.value
    .filter((f) => f.fillable)
    .filter((f) => {
      const accessGrant = f.accessGrants.find(
        (ag) => ag.businessUnitId === job.value.division?.ownerId
      )
      if (!accessGrant) return false

      const userRoles = getRolesForBusinessUnit.value(
        job.value?.division.ownerId
      )

      return userRoles.some(
        (role) => !accessGrant.roleRestrictions.includes(role)
      )
    })
})

const adhocFormSubmissions = computed(() => {
  return job.value.formSubmissions
    .filter(
      (fs) =>
        job.value.associatedForms.findIndex((f) => f.formUuid === fs.formId) ===
        -1
    )
    .map((fs) => {
      return {
        ...fs,
        formSubmissionId: fs.formSubmissionUuid,
        formName: getFormDefinitionById.value(fs.formId)?.name ?? 'Form',
      }
    })
})

const filledJobForm = computed(() => {
  return job.value.associatedForms
    .map((form) => {
      const submission = job.value.formSubmissions.find(
        (s) => s.formId === form.formUuid
      )

      return submission
        ? Object.assign({}, form, submission, { isFilledOut: true })
        : null
    })
    .filter((f) => f !== null)
})

const totalDistance = computed<number>(() => {
  const jobDistanceKm =
    job.value.totalDistance > 0
      ? job.value.totalDistance / 1000
      : tripDistance.value

  return tripMapDistanceUnit.value === TripMapDistanceUnits.MILES
    ? (jobDistanceKm ?? 0) * kmToMilesConversion
    : jobDistanceKm ?? 0
})

const formattedTripDistance = computed<string>(() => {
  if (tripDistance.value === undefined) {
    return 'Traveled ~'
  }

  const unitLabel =
    tripMapDistanceUnit.value === TripMapDistanceUnits.MILES
      ? 'miles'
      : 'kilometers'

  return `Traveled ${totalDistance.value.toFixed(1)} ${unitLabel}`
})

const canEditCompletedJobActualTimes = computed(
  () =>
    showEditButton.value &&
    jobPlannedActualTimesEditingEnabled.value &&
    canModifyCompletedJobActualTimes.value(job.value.hubId, job.value.accountId)
)

const canViewLineItems = computed(() => {
  if (!lineItemsFeatureFlag.value) return false
  if (job.value.serviceProviderBusinessUnitId === null) return false

  const hub = getBusinessUnitById.value(job.value.hubId)
  const isAssignedInternally =
    !!hub?.ownerId && job.value.serviceProviderBusinessUnitId === hub.ownerId

  return !isAssignedInternally
})

const canEditLineItems = computed(() => {
  if (job.value.serviceProviderBusinessUnitId === null) return false
  const { currentOrg } = useCurrentOrg()

  return (
    job.value.serviceProviderBusinessUnitId === currentOrg.value?.id &&
    !job.value.isApproved
  )
})

const { useActions } = createNamespacedHelpers('jobs')
const { attachFormSubmissionId, attachFormRequirementSubmission } = useActions([
  'attachFormSubmissionId',
  'attachFormRequirementSubmission',
])



const dismiss = function () {
  emit('dismiss')
}

const setSnackbarData = function ({
  type = SnackType.INFO,
  messageKey = null,
  customMessage = null, // customMessage will override messageKey unless customMessage = null
}: {
  type?: SnackType
  messageKey?: string | null
  customMessage?: string
}) {
  snackbarType.value = type
  if (customMessage) {
    customSnackbarMessage.value = customMessage
  } else {
    snackbarMessageKey.value = messageKey
    customSnackbarMessage.value = null
  }
  isSnackbarVisible.value = true
}

const jobDetailsUpdated = async function () {
  emit('refresh-job-history')
  emit('update-readonly-job')
  setTimeout(() => setSnackbarData({ messageKey: 'detailsUpdated' }), 250)
}

const jobPlannedActualTimesUpdated = async function () {
  emit('refresh-job-history')
  emit('update-readonly-job')
  setTimeout(
    () => setSnackbarData({ messageKey: 'plannedOrActualTimesUpdated' }),
    250
  )
}

const jobLocationsAndInventoryUpdated = async function () {
  emit('refresh-job-history')
  emit('update-readonly-job')
  setTimeout(
    () => setSnackbarData({ messageKey: 'locationsInventoryUpdated' }),
    250
  )
}

const addSignature = () => {
  emit('refresh-job-history')
  emit('update-readonly-job')
  setTimeout(() => setSnackbarData({ messageKey: 'signatureAdded' }), 250)
}

const clearSignature = () => {
  emit('refresh-job-history')
  emit('update-readonly-job')
  setTimeout(() => setSnackbarData({ messageKey: 'signatureCleared' }), 250)
}

const jobTagsUpdated = async () => {
  emit('refresh-job-history')
  emit('update-readonly-job')
  setTimeout(() => setSnackbarData({ messageKey: 'tagsUpdated' }), 250)
}

const handleAddToBatchSuccess = async () => {
  emit('refresh-job-history')
  emit('update-readonly-job')
  setTimeout(() => setSnackbarData({ messageKey: 'jobAddedToBatch' }), 250)
  hideAddToBatchBottomSheet()
}

const handleApprovalRejectedSuccess = async () => {
  closeApprovalRejectionBottomsheet()
  emit('refresh-job-history')
  emit('update-readonly-job')
  setSnackbar({ type: SnackType.INFO, messageKey: 'rejected' })
}

const handleJobAddedFailure = async () => {
  emit('update-readonly-job')
  setTimeout(
    () =>
      setSnackbarData({
        type: SnackType.ERROR,
        messageKey: 'jobAddedToBatchFailure',
      }),
    250
  )
}

function showLineItemBottomSheet() {
  isEditingLineItems.value = true
}

function hideLineItemBottomSheet() {
  isEditingLineItems.value = false
}

function showAddToBatchBottomSheet() {
  isAddingToBatch.value = true
}

function hideAddToBatchBottomSheet() {
  isAddingToBatch.value = false
}

function openApprovalRejectionBottomSheet() {
  isRejectingApproval.value = true
}

function closeApprovalRejectionBottomsheet() {
  isRejectingApproval.value = false
}

function showSignatureBottomSheet() {
  isAddingSignature.value = true
}

function closeSignatureBottomSheet() {
  isAddingSignature.value = false
}

/**
 * Goes up the parent component chain to tell `Ticket.vue` to update the schema ID.
 */
function updateSchemaButtonClicked() {
  emit('update-schema-button-clicked')
}

const setEmailDialogVisibility = (isVisible: boolean) => {
  isEmailDialogVisible.value = isVisible
}

const handleNotificationSent = () => {
  setSnackbarData({ messageKey: 'notificationSent' })
}

const dismissJobDetailsEdit = () => {
  isEditingDetails.value = !isEditingDetails.value
  emit('reset-custom-fields-schema-id')
}
const toggleJobLocationsAndInventoryEdit = () => {
  isEditingLocationsAndInventory.value = !isEditingLocationsAndInventory.value
}
const toggleJobTagsEdit = () => {
  isEditingTags.value = !isEditingTags.value
}
const toggleFormSelection = () => {
  isSelectingForm.value = !isSelectingForm.value
}
const showFormSubmission = (formSubmissionUuid: string) => {
  selectedFormSubmissionId.value = formSubmissionUuid
}
const editJobFormSubmission = (formId, formSubmissionUuid: string) => {
  isEditingJobFormSubmission.value = true
  selectedFormId.value = formId
  selectedFormSubmissionId.value = formSubmissionUuid
}
const closeFormSubmission = () => {
  selectedFormSubmissionId.value = null
}
const fillFormRequirement = (requirement: JobAssignmentFormRequirement) => {
  isFillingFormRequirementSubmission.value = true
  selectedFormRequirement.value = requirement
}
const viewFormRequirementSubmission = (
  requirement: JobAssignmentFormRequirement
) => {
  selectedFormRequirement.value = requirement
}
const closeFillFormRequirement = () => {
  selectedFormRequirement.value = null
  isFillingFormRequirementSubmission.value = false
}
const closeFormRequirementSubmission = () => {
  selectedFormRequirement.value = null
}
const selectNewForm = ({ formId }) => {
  isSelectingForm.value = false
  selectedFormId.value = formId
}
const closeSchemaBasedForm = () => {
  selectedFormId.value = null
  selectedFormSubmissionId.value = null
  isEditingJobFormSubmission.value = false
}
const attachFormSubmission = async (formSubmissionId: string) => {
  try {
    await attachFormSubmissionId({
      jobId: props.jobUuid,
      formId: selectedFormId.value,
      formSubmissionId,
    })
    setTimeout(() => setSnackbarData({ messageKey: 'attachedForm' }), 250)
  } catch (error) {
    setTimeout(
      () =>
        setSnackbarData({
          type: SnackType.ERROR,
          messageKey: 'attachFormError',
        }),
      250
    )
  } finally {
    selectedFormId.value = null
  }
}

const attachFormRequirement = async (formSubmissionId: string) => {
  if (selectedFormRequirement.value === null) return

  try {
    await attachFormRequirementSubmission({
      jobId: props.jobUuid,
      formRequirementId: selectedFormRequirement.value.id,
      formSubmissionId,
    })
    setTimeout(
      () => setSnackbarData({ messageKey: 'attachedFormRequirement' }),
      250
    )
  } catch (error) {
    setTimeout(
      () =>
        setSnackbarData({
          type: SnackType.ERROR,
          messageKey: 'attachFormRequirementError',
        }),
      250
    )
  } finally {
    isFillingFormRequirementSubmission.value = false
    selectedFormId.value = null
  }
}

const updatedAttachedFormSubmission = () => {
  closeFormSubmission()
  emit('update-readonly-job')
  setSnackbarData({
    messageKey: 'updatedAttachedFormSubmission',
  })
}

const updatedFormRequirementSubmission = () => {
  closeFormRequirementSubmission()
  setSnackbarData({
    messageKey: 'updatedFormRequirementSubmission',
  })
}

const unsupportedFileSelected = (messageKey: string) => {
  setSnackbarData({ type: SnackType.ERROR, messageKey })
}

const errorUploadingAttachment = (messageKey: string) => {
  setSnackbarData({ type: SnackType.ERROR, messageKey })
}

const getAdhocFormSubmissionSecondaryText = (formSubmission) => {
  const userName = getUserBySid.value(formSubmission.submittedBy)?.name
  const uploadedTime = formatDateTime(formSubmission.submittedOn)

  return userName ? `${userName} on ${uploadedTime}` : `${uploadedTime}`
}
const createComment = (comment) => {
  emit('create-comment', comment)
}

const setTripDistance = (distance: number) => {
  tripDistance.value = distance
}
const showJobPlannedActualTimesEdit = () => {
  isEditingPlannedActualTimes.value = true
}
const closeJobPlannedActualTimesEdit = () => {
  isEditingPlannedActualTimes.value = false
}

const handleUpdateReadonlyJob = () => {
  emit('update-readonly-job')
}

const openSendEmailForApprovalBottomSheet = () => {
  isSendingEmailForApproval.value = true
}
const closeSendEmailForApprovalBottomSheet = () => {
  isSendingEmailForApproval.value = false
}
const submittedForApproval = () => {
  emit('refresh-job-history')
  emit('update-readonly-job')
  setTimeout(
    () =>
      setSnackbarData({
        type: SnackType.INFO,
        messageKey: 'submittedForApproval',
      }),
    250
  )
}

function setSnackbar({
  type = SnackType.INFO,
  messageKey = null,
  customMessage = null, // customMessage will override messageKey unless customMessage = null
}: {
  type?: SnackType
  messageKey?: string | null
  customMessage?: string
}) {
  setTimeout(() => setSnackbarData({ type, messageKey, customMessage }), 250)
}

expose({ rootSchema })

return { __sfc: true,props, isEditingPlannedActualTimes, isEmailDialogVisible, isSendingEmailForApproval, isEditingDetails, isEditingLocationsAndInventory, isEditingJobFormSubmission, isFillingFormRequirementSubmission, isEditingTags, selectedFormSubmissionId, isSelectingForm, selectedFormId, selectedFormRequirement, isSnackbarVisible, customSnackbarMessage, tripDistance, snackbarMessageKey, snackbarType, isEditingLineItems, isAddingToBatch, isRejectingApproval, isAddingSignature, snackbarMessages, store, route, jobNumber, showTripMapRef, shouldFetchGeoJsonManually, jobGeoJson, lineItemsFeatureFlag, getUserBySid, canModifyCompletedJobsPendingApproval, canModifyCompletedJobsWithNoApproval, getBusinessUnitById, customFieldsSchema, getTaskById, getCostCentersForHub, getForms, getFormDefinitionById, getRolesForBusinessUnit, canModifyCompletedJobActualTimes, getByChildHubId, jobPlannedActualTimesEditingEnabled, tripMapDistanceUnit, job, isBottomSheetVisible, displayedJobId, finalStatus, projectSideSheetRoute, title, subtitle, jobCrewNames, formattedPlannedTime, formattedActualTime, formattedPlannedDuration, formattedActualDuration, showEditButton, costCenterDescription, jobIsApproved, jobRequesterName, hub, company, jobDivisionPath, followersLabel, rootSchema, schema, uiSchema, flattenedFormFields, canDeleteFormSubmissions, fillableForms, adhocFormSubmissions, filledJobForm, totalDistance, formattedTripDistance, canEditCompletedJobActualTimes, canViewLineItems, canEditLineItems, useActions, attachFormSubmissionId, attachFormRequirementSubmission, emit, dismiss, setSnackbarData, jobDetailsUpdated, jobPlannedActualTimesUpdated, jobLocationsAndInventoryUpdated, addSignature, clearSignature, jobTagsUpdated, handleAddToBatchSuccess, handleApprovalRejectedSuccess, handleJobAddedFailure, showLineItemBottomSheet, hideLineItemBottomSheet, showAddToBatchBottomSheet, hideAddToBatchBottomSheet, openApprovalRejectionBottomSheet, closeApprovalRejectionBottomsheet, showSignatureBottomSheet, closeSignatureBottomSheet, updateSchemaButtonClicked, setEmailDialogVisibility, handleNotificationSent, dismissJobDetailsEdit, toggleJobLocationsAndInventoryEdit, toggleJobTagsEdit, toggleFormSelection, showFormSubmission, editJobFormSubmission, closeFormSubmission, fillFormRequirement, viewFormRequirementSubmission, closeFillFormRequirement, closeFormRequirementSubmission, selectNewForm, closeSchemaBasedForm, attachFormSubmission, attachFormRequirement, updatedAttachedFormSubmission, updatedFormRequirementSubmission, unsupportedFileSelected, errorUploadingAttachment, getAdhocFormSubmissionSecondaryText, createComment, setTripDistance, showJobPlannedActualTimesEdit, closeJobPlannedActualTimesEdit, handleUpdateReadonlyJob, openSendEmailForApprovalBottomSheet, closeSendEmailForApprovalBottomSheet, submittedForApproval, setSnackbar, length, BatchSelectionPage, FormLayout, FormSubmission, NewFormSelectionBottomSheet, SchemaBasedForm, ApprovalRejectionForm, JobCardLocationsDetail, JobSignature, JobSignatureForm, LineItemForm, LineItemsCard, ProjectBreadcrumbs, VsSnackbar, VsText, JobActivityLog, AssignmentFormRequirements, AttachmentCard, AttachmentPicker, Attachments, JobDetailsEdit, JobInlineForm, JobLocationsAndInventoryEditSaveWrapper, JobPlannedActualTimesEdit, JobStatusChip, JobTagsEdit, JobTripMap, ReadonlyJobFormActionBar, SendEmailDialog, SendForApprovalBottomSheet, VsBottomSheet, JobReferencesCard }
}

})